import type { JSX } from 'react';
import { Alert, Spin } from 'antd';
import { Navigate, Route, Routes } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { authApi, hasPermission, type VkAppUser } from './lib/authApi';
import { ApiError } from './lib/api';
import { Layout } from './components/Layout';
import { LoginPage } from './pages/LoginPage';
import { GroupsPage } from './pages/GroupsPage';
import { GroupSettingsPage } from './pages/GroupSettingsPage';
import { FeedPage } from './pages/FeedPage';
import { TrainingPage } from './pages/TrainingPage';

/**
 * vk.brasol.ru — «ВК-бот комментариев».
 *
 * Auth-гейт: GET /api/auth/me (общая CRM-сессия, cookie domain=.brasol.ru).
 * 401 → форма логина (тот же /api/auth/login). Доступ к страницам —
 * permission vk_bot:view (выдаётся ролям в CRM «Настройки → Роли»).
 */
export function App(): JSX.Element {
  const meQuery = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => authApi.me(),
    retry: (count, err) =>
      !(err instanceof ApiError && err.status === 401) && count < 1,
  });

  if (meQuery.isLoading) {
    return <Spin size="large" style={{ display: 'block', margin: '160px auto' }} />;
  }

  const authError = meQuery.error;
  if (authError instanceof ApiError && authError.status === 401) {
    return <LoginPage onLoggedIn={() => meQuery.refetch()} />;
  }
  if (meQuery.isError || !meQuery.data) {
    return (
      <Alert
        type="error"
        showIcon
        style={{ maxWidth: 480, margin: '120px auto' }}
        message="Сервис недоступен"
        description="Не удалось проверить сессию. Обновите страницу или попробуйте позже."
      />
    );
  }

  const user: VkAppUser = meQuery.data.user;
  if (!hasPermission(user, 'vk_bot:view')) {
    return (
      <Alert
        type="warning"
        showIcon
        style={{ maxWidth: 520, margin: '120px auto' }}
        message="Нет доступа"
        description="Для входа нужно право «ВК-бот комментариев — просмотр». Попросите администратора выдать его вашей роли в CRM (Настройки → Роли)."
      />
    );
  }

  return (
    <Layout user={user}>
      <Routes>
        <Route path="/" element={<GroupsPage user={user} />} />
        <Route path="/groups/:id" element={<GroupSettingsPage user={user} />} />
        <Route path="/feed" element={<FeedPage />} />
        <Route path="/training" element={<TrainingPage user={user} />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Layout>
  );
}
